<!-- Displays an image of a currently configured product. -->

<template>
    <div
        id="cz2__renderer"
        :class="[
            'cz2__renderer',
            `cz2__renderer--${screenLocation}`,
            {
                'cz2__renderer--started': true || isStarted,
                'cz2__renderer--kiosk': isKiosk,
            },
        ]"
        :style="placeholderBasedStyle"
    >
        <div
            class="cz2__renderer__body"
            :key="'controller'"
        >
            <renderer-image
                :controller="currentController"
                :view="currentView"
                :onlyController="viewController"
            />
        </div>

        <transition name="cz2__fade">
            <renderer-controls v-if="show360Controls" />
        </transition>
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__renderer {
            position: absolute;
            top: 44px;
            left: 50%;
            transform: translateX(-50%);

            width: 100%;
            // Full height - steps options height - top & bottom margins - logo height
            height: calc(100% - var(--steps-options-height) - 44px);

            opacity: 0;
            transition: opacity $transition-fast ease-out, top $transition-fast ease-out, transform $transition-fast ease-out;
            pointer-events: none;

            background-image: url(../assets/images/pop-gradient.png);
            background-size: 100% 100%;

            @include respond-above(lg) {
                top: auto;
                bottom: 0;
                left: 0;
                transform: none;

                width: 50%;
                height: calc(100% - var(--layout-spacing) - 44px);
            }

            @include respond-above(xl) {
                height: calc(100% - (var(--layout-spacing) * 2) - 71px);
            }

            &.cz2__renderer--started {
                opacity: 1;

                pointer-events: auto;
                cursor: grab;

                &:active {
                    cursor: grabbing;
                }
            }

            &.cz2__renderer--topmost {
                top: 50px;

                .cz2__renderer__360 {
                    bottom: 210px;
                }
            }

            &.cz2__renderer--name,
            &.cz2__renderer--review,
            &.cz2__renderer--checkout,
            &.cz2__renderer--confirm {
                background-image: none;
                // Can't have zindex here.
                z-index: 1;
            }

            &.cz2__renderer--kiosk.cz2__renderer--checkout {
                z-index: 1;
                transition: none;
            }

            &.cz2__renderer--checkout,
            &.cz2__renderer--review {
                .cz2__renderer__360 {
                    bottom: -40px;
                }
            }

            &.cz2__renderer--name {
                .cz2__renderer__360 {
                    bottom: -40px;
                }
            }

            // &.cz2__renderer--review {
            //     --renderer-height: calc(100% - (var(--steps-options-nav-height) * 6));

            //     top: calc((var(--steps-options-nav-height) * 2) + 30px);

            //     height: var(--renderer-height);

            //     .cz-client-camera-view {
            //         height: calc(100% - 35px);
            //     }

            //     @include respond-above(lg) {
            //         --renderer-height: calc(100% - (var(--steps-options-nav-height) * 3));

            //         top: calc(var(--steps-options-nav-height) - var(--layout-spacing));
            //     }
            // }

            &.cz2__renderer--checkout {
                &.cz2__renderer--started {
                    cursor: auto;
                    pointer-events: none;

                    // Don't reserve space for 360 icons that aren't there
                    .cz-client-camera-view {
                        height: 100%;
                    }
                }
            }

            // &.cz2__renderer--confirm {
            //     top: 450px;
            //     height: 800px;

            //     .cz2__renderer__360 {
            //         bottom: 0;
            //     }
            // }

            &.cz2__renderer--popup {
                top: 400px;

                z-index: 2000;

                .cz2__renderer__360 {
                    bottom: 180px;
                }
            }
        }

        .cz2__renderer__360 {
            position: absolute;
            left: 50%;
            bottom: 16px;
            transform: translate(-50%, 0);

            @include respond-above(md) {
                bottom: var(--layout-spacing);
            }
        }

        .cz2__renderer__body,
        .cz2__renderer__content {
            position: absolute;

            left: 0;
            top: 0;

            width: 100%;
            height: 100%;
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__renderer {
            width: 100%;
            height: calc(100% - var(--steps-options-height));
            top: 0px;

            // Need to show it above the contact step.
            // z-index: 1;
        }
    }

    #cz2.cz2__is-sb {
        .cz2__renderer {
            @include bg(transparent);
        }
    }

    #cz2.cz2.cz2__is-sb.cz2__is-name-step {
        .cz2__renderer {
            background-image: none;
        }
    }

    #cz2.cz2.cz2__is-sb.cz2.cz2__is-kiosk {
        .cz2__renderer {
            height: calc(100% - var(--steps-options-height) - 64px); // for border
        }
    }

    #cz2.cz2.cz2__is-sb.cz2.cz2__is-kiosk.cz2__is-checkout-step {
        .cz2__renderer {
            height: calc(100% - var(--steps-options-height) - 64px); // for border
            left: 0 !important;
            // top: 165px !important;
        }
    }
</style>

<script>
    import ResizeObserver from 'resize-observer-polyfill';

    import * as constants from '../constants';

    import RendererImage from './RendererImage.vue';
    import RendererControls from './RendererControls.vue';

    export default {
        components: {
            RendererImage,
            RendererControls,
        },

        data() {
            return {
                currentController: null,
                currentView: null,

                // Watch placeholder position and size.
                placeholderBasedStyle: null,

                overrideZIndex: null,
            };
        },

        computed: {
            /**
             * Current controller.
             */
            mainController() {
                return this.$store.state.customizer.mainController;
            },

            /**
             * Viewable controller
             */
            viewController() {
                // Show the complete set on Review and Checkout.
                if (this.isReviewStep) {
                    return null;
                }

                if (this.isCheckoutStep) {
                    return null;
                }

                if (this.isContactStep) {
                    return null;
                }

                if (this.isConfirmStep) {
                    return null;
                }

                return this.$store.state.customizer.viewController;
            },

            isStarted() {
                return this.$store.getters.isStarted;
            },

            isTwoPack() {
                return this.$store.getters.isTwoPack;
            },

            isNameStep() {
                return this.currentGroupCode === 'Name';
            },

            isReviewStep() {
                return this.currentGroupCode === 'Review';
            },

            isContactStep() {
                return this.currentGroupCode === 'Contact';
            },

            isCheckoutStep() {
                return this.currentGroupCode === 'Checkout';
            },

            isConfirmStep() {
                return this.currentGroupCode === 'Confirm';
            },

            show360Controls() {
                return this.isStarted && !this.isCheckoutStep && !this.isContactStep && !this.isConfirmStep;
            },

            currentGroup() {
                return this.$store.state.customizer.currentGroup;
            },

            currentGroupCode() {
                return this.currentGroup?.code;
            },

            // Pick out the screen size and location based on the currently selected group.
            screenLocation() {
                if (this.isCheckoutStep) {
                    return 'checkout';
                }

                if (this.isReviewStep) {
                    return 'review';
                }

                if (this.isNameStep) {
                    return 'name';
                }

                if (this.isContactStep) {
                    return 'name';
                }

                if (this.currentGroupCode === 'Contact') {
                    return 'topmost';
                }

                if (this.currentGroupCode === 'Confirm') {
                    return 'confirm';
                }

                return 'standard';
            },

            preferredView() {
                return this.$store.state.preferredView;
            },

            forcedView() {
                return this.$store.state.forcedView;
            },

            isBaby() {
                return this.$store.getters.isBaby;
            },

            selectedView() {
                if (this.forcedView) {
                    return this.forcedView;
                }

                if (constants.HEAD_STEPS.includes(this.currentGroupCode)) {
                    if (this.isBaby) {
                        return 'HeadBaby';
                    }

                    return 'Head';
                }

                if (this.isReviewStep) {
                    if (this.preferredView) {
                        return this.preferredView;
                    }

                    return this.isTwoPack ? 'BoxFarPack' : 'BoxFar';
                }

                if (this.isNameStep) {
                    if (this.isTwoPack) {
                        return 'Main Mini';
                    }

                    return 'BoxName';
                }

                if (this.isCheckoutStep) {
                    if (this.isTwoPack) {
                        return 'CheckoutPack';
                    }

                    return 'BoxNameZoomed';
                }

                if (this.isContactStep) {
                    if (this.isTwoPack) {
                        return 'MainMiniPack';
                    }

                    return 'BoxName';
                }

                if (this.isConfirmStep) {
                    if (this.isTwoPack) {
                        return 'MainMiniPack';
                    }

                    return 'Main';
                }

                return 'Main';
            },

            options() {
                return this.$store.state.customizer.options;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },
        },

        watch: {
            /**
             * Load image view for the new controller.
             */
            mainController(newController, oldController) {
                this.disconnect(oldController);

                this.load(newController);
            },

            selectedView() {
                this.updateViews();
            },
        },

        mounted() {
            // Paint the main controller.
            if (this.mainController) {
                this.mountController(this.mainController);
            }

            // Follow the placeholder instead of crusty old logic.
            this.emitter.$on('setRendererPlaceholder', this.setPlaceholder);
        },

        beforeDestroy() {
            this.emitter.$off('setRendererPlaceholder', this.setPlaceholder);

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();

                clearInterval(this.resizeInterval);
            }
        },

        methods: {
            /**
             * Disconnects image view from the controller and stops listening to events.
             */
            disconnect() {
            },

            /**
             * Loads image view for the given controller.
             */
            load(controller) {
                if (controller) {
                    this.mountController(controller);
                }
            },

            /**
             * Create image view.
             */
            mountController(controller) {
                this.currentController = controller;

                if (controller) {
                    // Find some view.
                    const view = controller.blueprint.views.find((v) => v.custom && v.custom['fun-view-type'] === 'main');

                    this.currentView = view ? view.code : null;
                } else {
                    this.currentView = null;
                }
            },

            /**
             * Updates view slides visibility.
             */
            updateViews() {
                this.currentView = this.selectedView;
            },

            setPlaceholder(placeholder) {
                if (placeholder == null || placeholder.owner == null) {
                    // Must provide an owner now.
                    return;
                }

                // Set new placeholder.
                if (placeholder.placeholder) {
                    // Clear out the old stuff.
                    if (this.placeholder) {
                        // Accept new placeholders regardless of the old owner.
                        this.setPlaceholder({
                            owner: this.placeholderOwner,
                        });
                    }

                    this.placeholder = placeholder.placeholder;
                    this.placeholderOwner = placeholder.owner;

                    //  Watch new element.
                    // TODO: Eventually, we should replace the old logic with the placeholder based stuff for all screens.
                    if (this.resizeObserver) {
                        this.resizeObserver.disconnect();

                        clearInterval(this.resizeInterval);
                    }

                    this.resizeObserver = new ResizeObserver(() => {
                        this.resizeToPlaceholder();
                    });

                    this.resizeObserver.observe(this.placeholder);

                    this.resizeToPlaceholder(placeholder?.immediate);
                } else if (placeholder.owner === this.placeholderOwner) {
                    // Only if the owner matches.

                    // Stop watching and return to the old sizing logic.
                    if (this.resizeObserver) {
                        this.resizeObserver.disconnect();

                        clearInterval(this.resizeInterval);
                    }

                    this.placeholder = null;
                    this.resizeObserver = null;
                    this.placeholderBasedStyle = null;
                }
            },

            resizeToPlaceholder() {
                let x = 0;
                let y = 0;
                let frameHeight = 0;

                let container = this.placeholder;

                while (container) {
                    x += container.offsetLeft;
                    y += container.offsetTop;

                    if (container.classList.contains('swiper-wrapper')) {
                        const offsetY = container.parentNode?.swiper?.translate;
                        if (offsetY != null) {
                            y += offsetY;
                        }
                    }

                    container = container.offsetParent;

                    // Stop at frame.
                    if (container?.classList.contains('cz2__frame')) {
                        frameHeight = container.offsetHeight;

                        break;
                    }
                }

                if (frameHeight === 0) {
                    // Don't use window.innerHeight, account for dynamic height sizing.
                    frameHeight = document.querySelector('#cz2.cz2').offsetHeight;
                }

                // In some cases... the placeholder is below the fold since other steps are still animating.
                // So we push the renderer back into the view.
                // Kind of hacky... need a more precise method.
                if (y >= frameHeight) {
                    y -= frameHeight;
                }

                const width = this.placeholder.offsetWidth;
                const height = this.placeholder.offsetHeight;

                const values = {
                    left: `${x}px`,
                    top: `${y}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                    transform: 'none',
                };

                this.placeholderBasedStyle = values;
            },
        },
    };
</script>
